<template>
    <div v-bind="$attrs">
    <aside :class="searchBoxClass" class="pt-32 pb-16 searchbox shadow-lg">
        <UiContentwrapper>
            <div class="w</aside>-full col-span-8 col-start-2 ">
                <form @submit.prevent>
                    <input aria-label="Produktsökfält" ref="searchInput" v-model="searchterm"
                        @keydown.esc="$emit('closeSearch')" class="searchfield text-4xl" type="text"
                        placeholder="Type to search..." />
                </form>
            </div>
            <button aria-label="Close search" class="close col-span-1" @click="$emit('closeSearch')">
                <UiClose />
            </button>
        </UiContentwrapper>

        <div v-if="searchterm.length < 3" class="center mt-4" v-motion-pop>
            <p v-if="searchterm.length > 0" class="small">Type at least 3 characters to search</p>
        </div>
        <div v-else-if="isSearching" class="center mt-4" v-motion-pop>
            <p class="small">Searching...</p>
        </div>
        <template v-else-if="results.length > 0" v-motion-pop>
            <SearchSuggestions @newsearch="onSuggestionClick" :results="results" :searchterm="searchterm" />
            <SearchResults :results="limitedResults" @closeSearchXX="$emit('closeSearch')" />
        </template>
        <div v-else class="center mt-4" v-motion-pop :delay="200">
            <p class="small">Nothing found</p>
        </div>
    </aside>
    </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useDebounceFn, useWindowSize } from '@vueuse/core';
import { useSearch } from '@/composables/search';
import { usePageStore } from '@/stores/pageStore';
import { mapProductToGa4Item } from '@/utils/ga4Map'

const UiClose = defineAsyncComponent(() => import('@/components/ui/close.vue'));


const { proxy } = useScriptGoogleTagManager();

const pageStore = usePageStore();
const isSearching = ref(false);

const searchInput = ref<HTMLInputElement | null>(null);
const searchterm = ref('');
const router = useRouter();

const { search } = useSearch();
const results = ref([]);

const matchLimit = 99;
const limitedResults = computed(() => {
    return results.value.slice(0, matchLimit);
});

const { width } = useWindowSize();

// Fokusera input vid öppning
onMounted(() => {
    pageStore.fetchAllPages();
    nextTick(() => {
        // Only focus if screen is wider than 600px (not small/mobile)
        if (width.value > 600 && searchInput.value) {
            searchInput.value.focus();
        }
    });
});

// Debounced sökning
const runSearch = useDebounceFn(async () => {
    isSearching.value = true;
    proxy.dataLayer.push({
        event: 'Site Search',
        searchTerms: searchterm.value,
        pageUrl: window.location.href
    })
    results.value = await search(searchterm.value);
    isSearching.value = false;

    try {
        const productResults = results.value.filter((r: any) => r.type === 'product');
        if (!productResults.length) return;

        const products = productResults.map((r: any) => r.product ?? r);
        const listId = 'search_results';
        const listName = window.location.pathname || '/search';

        const items = products
            .map((p: any, i: number) =>
                mapProductToGa4Item({
                    product: p,
                    index: i,
                    listId,
                    listName
                })
            )
            .filter(Boolean);

        if (!items.length) return;

        const currency =
            products[0]?.priceRange?.minVariantPrice?.currencyCode ??
            products[0]?.variants?.edges?.[0]?.node?.price?.currencyCode ??
            'EUR';

        console.log('Pushing product search analytics to dataLayer', {
            currency,
            items
        });

        proxy.dataLayer.push({
            event: 'view_item_list',
            ecommerce: {
                currency,
                items
            },
            pageUrl: window.location.href
        })
    } catch (err) {
        // Don't break the search if tracking fails
        // eslint-disable-next-line no-console
        console.warn('Error while pushing product search analytics', err);
    }


}, 250);

const onSuggestionClick = (title: string) => {
    console.log('Suggestion clicked:', title);
    searchterm.value = title;
    results.value = []; // Rensa gamla resultat direkt
    runSearch(); // Trigga direkt sökning
};

watch(searchterm, (val) => {
    if (val.length >= 3) {
        runSearch();
    } else {
        results.value = [];
    }
});

const setFocus = () => {
    if (!isTouchDevice.value && searchInput.value) {
        searchInput.value.focus();
    }
};




const isTouchDevice = computed(() => {
    if (!process.client) return false;
    return window.ontouchstart !== undefined;
});

const isExpanded = computed(() => router.currentRoute.value.path === '/search');

const searchBoxClass = computed(() => {
    let classes = isExpanded.value ? 'searchbox expanded' : 'searchbox';

    if (isSearching.value) {
        classes += ' cursor-wait';
    }
    return classes;
});

const iconFor = (type: string): string => {
    switch (type) {
        case 'product':
            return '🛒';
        case 'collection':
            return '📁';
        case 'article':
            return '📰';
        case 'page':
            return '📄';
        default:
            return '🔎';
    }
};
</script>

<style scoped>
.searchbox {
    position: fixed;
    overflow-y: auto;
    height: 100vh;
    z-index: 10;
    top: 0;
    left: 0;
    width: 100vw;

    background-color: #ffffff;

}

.searchbox.expanded {
    border: none;
}



.searchfield {
    width: 100%;
    border: none;
    background-color: transparent;
    outline: none;
    text-align: center;
}

::placeholder {
    color: #ab9670;
}

.center {
    text-align: center;
}

.seeall {
    font-size: 1.25rem;
    text-align: center;
    padding: 20px 0;
    text-transform: uppercase;
    text-decoration: underline;
}
</style>